// `firebase/functions` stand-in: POST /fn/<name> with the callable envelope.
import { FirebaseError, apiFetch, getApp, type FirebaseApp } from './app';

export interface Functions { app: FirebaseApp; region: string; customDomain: string | null }
export interface HttpsCallableOptions { timeout?: number; limitedUseAppCheckTokens?: boolean }
export interface HttpsCallableResult<R = unknown> { data: R }
export type HttpsCallable<T = unknown, R = unknown> = (data?: T | null) => Promise<HttpsCallableResult<R>>;

export class FunctionsError extends FirebaseError {
  constructor(code: string, message: string, readonly details?: unknown) { super(`functions/${code}`, message, { details }); this.name = 'FunctionsError'; }
}

const CANONICAL: Record<string, string> = {
  OK: 'ok', CANCELLED: 'cancelled', UNKNOWN: 'unknown', INVALID_ARGUMENT: 'invalid-argument', DEADLINE_EXCEEDED: 'deadline-exceeded',
  NOT_FOUND: 'not-found', ALREADY_EXISTS: 'already-exists', PERMISSION_DENIED: 'permission-denied', UNAUTHENTICATED: 'unauthenticated',
  RESOURCE_EXHAUSTED: 'resource-exhausted', FAILED_PRECONDITION: 'failed-precondition', ABORTED: 'aborted', OUT_OF_RANGE: 'out-of-range',
  UNIMPLEMENTED: 'unimplemented', INTERNAL: 'internal', UNAVAILABLE: 'unavailable', DATA_LOSS: 'data-loss',
};
const BY_STATUS: Record<number, string> = { 400: 'invalid-argument', 401: 'unauthenticated', 403: 'permission-denied', 404: 'not-found', 409: 'aborted', 429: 'resource-exhausted', 499: 'cancelled', 500: 'internal', 501: 'unimplemented', 503: 'unavailable', 504: 'deadline-exceeded' };

export function getFunctions(app: FirebaseApp = getApp(), regionOrCustomDomain = 'us-central1'): Functions {
  const custom = regionOrCustomDomain.startsWith('http') ? regionOrCustomDomain.replace(/\/$/, '') : null;
  return { app, region: custom ? 'us-central1' : regionOrCustomDomain, customDomain: custom };
}
export function connectFunctionsEmulator(): void { /* noop */ }

async function invoke<T, R>(app: FirebaseApp, url: string, data: T | null | undefined, opts?: HttpsCallableOptions): Promise<HttpsCallableResult<R>> {
  // One deadline over the whole call — the token refresh, the request AND reading the body. It used to
  // cover only the request headers, so a reply that stalled mid-body (or a token refresh that never
  // returned) hung forever and took the sync engine with it.
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      ctrl?.abort();
      const e = new Error('The request took too long'); e.name = 'AbortError';
      reject(e);
    }, opts?.timeout ?? 70_000);
  });
  let res: Response;
  let body: any;
  try {
    [res, body] = await Promise.race([
      (async (): Promise<[Response, any]> => {
        const r = await apiFetch(app, url, { method: 'POST', json: { data: data === undefined ? null : data }, signal: ctrl?.signal });
        const b = await r.json().catch((e: Error) => { if (e?.name === 'AbortError') throw e; return null; });
        return [r, b];
      })(),
      deadline,
    ]);
  } catch (e) {
    throw new FunctionsError((e as Error)?.name === 'AbortError' ? 'deadline-exceeded' : 'unavailable', (e as Error)?.message ?? 'network error');
  } finally { if (timer) clearTimeout(timer); }
  if (!res.ok || (body && body.error)) {
    const err = body?.error ?? {};
    const code = CANONICAL[err.status] ?? BY_STATUS[res.status] ?? 'unknown';
    throw new FunctionsError(code, err.message ?? `HTTP ${res.status}`, err.details);
  }
  return { data: (body && 'result' in body ? body.result : body?.data) as R };
}

export function httpsCallable<T = unknown, R = unknown>(fns: Functions, name: string, opts?: HttpsCallableOptions): HttpsCallable<T, R> {
  return (data?: T | null) => invoke<T, R>(fns.app, `/fn/${name}`, data, opts);
}
export function httpsCallableFromURL<T = unknown, R = unknown>(fns: Functions, url: string, opts?: HttpsCallableOptions): HttpsCallable<T, R> {
  const rel = url.startsWith(fns.app._baseUrl) ? url.slice(fns.app._baseUrl.length) : url;
  return (data?: T | null) => invoke<T, R>(fns.app, rel, data, opts);
}
